/**
 * Lock pinch-/double-tap-zoom in the installed PWA.
 *
 * iOS Safari deliberately IGNORES `maximum-scale`/`user-scalable=no` in the
 * viewport meta (accessibility), and it ignores it in the standalone PWA too —
 * so the meta alone can't stop the app being pinch-zoomed and then panned
 * sideways (the "floating chat" symptom). The reliable cross-version block is
 * to preventDefault the iOS gesture events + rapid double-tap.
 *
 * Scoped to standalone (home-screen) so the normal browser keeps full zoom for
 * accessibility. Listeners are passive:false (required to call preventDefault).
 */
export function lockPwaZoom(): void {
  if (typeof window === "undefined") return;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari exposes this non-standard flag on navigator.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  if (!standalone) return;

  const prevent = (e: Event) => e.preventDefault();
  // iOS-only pinch gesture events.
  document.addEventListener("gesturestart", prevent, { passive: false });
  document.addEventListener("gesturechange", prevent, { passive: false });
  document.addEventListener("gestureend", prevent, { passive: false });

  // Double-tap-to-zoom: swallow the second tap when it lands within 300ms.
  let lastTouchEnd = 0;
  document.addEventListener(
    "touchend",
    (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    },
    { passive: false },
  );
}
